import React from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { panelVisible, visibleActions } from '@/utils/budget-optimizer'
import { useBudgetOptimizer } from './hooks/useBudgetOptimizer'

interface BudgetOptimizerToggleProps {
    adCampaignId: string
    open: boolean
    onToggle: () => void
    /** Do not read the optimizer (campaigns it can never handle). */
    skip?: boolean
}

/**
 * The pictogram that opens and closes the budget optimizer of a campaign. It takes no room in the campaign list: the
 * panel is shown only while it is open. A dot tells that the optimizer waits for the owner.
 */
const BudgetOptimizerToggle: React.FC<BudgetOptimizerToggleProps> = ({ adCampaignId, open, onToggle, skip = false }) => {
    const { optimizer } = useBudgetOptimizer(adCampaignId, { skip })

    if (!optimizer || !panelVisible(optimizer)) return null

    const waiting = visibleActions(optimizer).length > 0
    const label = open ? 'Close budget optimizer' : 'Open budget optimizer'

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={label}
            title={label}
            data-testid="budget-optimizer-toggle"
            className="tw:relative tw:p-1 tw:rounded tw:hover:bg-transparent tw:transition-colors"
        >
            <SlidersHorizontal
                className={`tw:size-5 tw:cursor-pointer hover:tw:text-blue-300 ${open ? 'tw:text-white' : 'tw:text-blue-400'}`}
            />
            {waiting && (
                <span
                    aria-hidden="true"
                    data-testid="budget-optimizer-toggle-dot"
                    className="tw:absolute tw:right-0 tw:top-0 tw:size-2 tw:rounded-full tw:bg-yellow-300"
                />
            )}
        </button>
    )
}

export default BudgetOptimizerToggle
